/**
 * Haptic feedback for one-host gameplay.
 *
 * The host holds the phone and faces the group — they don't watch the
 * screen continuously. Haptic ticks let them FEEL the critical moments
 * (slot-machine settles, timer counting down, phase shift, consequence
 * reveal) without breaking eye contact with the room.
 *
 * `navigator.vibrate` is widely supported on Android Chrome but is a
 * no-op on iOS Safari (and desktop). We keep it best-effort: silent
 * fallthrough rather than feature-detect-and-warn — the visual layer
 * still carries the moment, haptic is a supplement, not a hard contract.
 */

function safeVibrate(arg: number | number[]): void {
  if (typeof navigator === "undefined") return;
  const v = navigator.vibrate?.bind(navigator);
  if (typeof v !== "function") return;
  try {
    v(arg);
  } catch {
    // Some browsers throw on tab-hidden / cross-origin frames — swallow.
  }
}

/** Single short tap. Default ~confirmation feel. */
export function tap(durationMs: number = 18): void {
  safeVibrate(durationMs);
}

/** A custom pulse pattern (vibrate, pause, vibrate, …) in ms. */
export function pattern(...durations: number[]): void {
  safeVibrate(durations);
}

/* ------------------------------------------------------------ presets */

/** Slot-machine just settled on a player name. Soft, satisfying click. */
export function pickSettle(): void {
  pattern(28);
}

/** Phase flash entrance — peak/engagement transition. Two-beat. */
export function phaseFlash(): void {
  pattern(40, 60, 40);
}

/** Reward reveal (DONE → 🏆). Three-beat ascending feel. */
export function reward(): void {
  pattern(20, 50, 20, 50, 60);
}

/** Penalty reveal (FAILED → 💀). Heavier, longer punch. */
export function penalty(): void {
  pattern(80, 40, 120);
}

/** Single tick on each of the last 3 timer seconds. */
export function timerTick(): void {
  tap(15);
}

/** Final long buzz when timer hits zero. */
export function timerExpire(): void {
  pattern(60, 40, 60, 40, 200);
}
